import { DEFAULT_DOCUMENT_VIEWPORT } from "@/document/model/document";
import { cameraFromDocumentViewport } from "@/editor/shell/canvasGeometry";
import { createDefaultCamera, type Camera } from "@/renderer/camera";
import type { RenderPreview } from "@/renderer/overlayTypes";
import type { RichTextModel } from "@/document/model/text";
import type { Rect } from "@/lib/math/rect";
import { StoreSubject } from "@/lib/store/StoreSubject";
import {
  cameraStateEqual,
  rectStateEqual,
  type ComponentPlacementState,
  type SculptBrushPreviewState,
  type VectorEdgeBendPreviewState,
  type VectorHandlePreviewState,
  type VectorTopologyPreviewState,
  type VectorVertexPreviewState,
  type ViewportTarget,
} from "./editorStoreTypes";

function previewArrayEqual(left: RenderPreview[], right: RenderPreview[]): boolean {
  if (left.length !== right.length) {
    return false;
  }
  for (let index = 0; index < left.length; index += 1) {
    const a = left[index]!;
    const b = right[index]!;
    if (
      a.nodeId !== b.nodeId
      || a.mode !== b.mode
      || a.rotation !== b.rotation
      || a.bounds.x !== b.bounds.x
      || a.bounds.y !== b.bounds.y
      || a.bounds.width !== b.bounds.width
      || a.bounds.height !== b.bounds.height
    ) {
      return false;
    }
  }
  return true;
}

export class ViewStore {
  readonly camera = new StoreSubject<Camera>(
    typeof window === "undefined"
      ? { ...createDefaultCamera() }
      : cameraFromDocumentViewport(DEFAULT_DOCUMENT_VIEWPORT),
    { equals: cameraStateEqual },
  );

  readonly hoveredNodeId = new StoreSubject<string | undefined>(undefined);
  readonly previews = new StoreSubject<RenderPreview[]>([], { equals: previewArrayEqual });
  readonly vertexPreview = new StoreSubject<VectorVertexPreviewState | undefined>(undefined);
  readonly handlePreview = new StoreSubject<VectorHandlePreviewState | undefined>(undefined);
  readonly edgeBendPreview = new StoreSubject<VectorEdgeBendPreviewState | undefined>(undefined);
  readonly sculptBrushPreview = new StoreSubject<SculptBrushPreviewState | undefined>(undefined);
  readonly vectorTopologyPreview = new StoreSubject<VectorTopologyPreviewState | undefined>(undefined);
  readonly textPreviews = new StoreSubject<Map<string, RichTextModel>>(new Map());
  readonly boundsPreview = new StoreSubject<Rect | undefined>(undefined, {
    equals: rectStateEqual,
  });
  readonly animationTimeMs = new StoreSubject(0);
  readonly fontsReadyToken = new StoreSubject(0);
  readonly resetZoomToken = new StoreSubject(0);
  readonly viewportTarget = new StoreSubject<ViewportTarget | undefined>(undefined);
  readonly viewportCenter = new StoreSubject<{ x: number; y: number } | undefined>(undefined);
  readonly viewportBounds = new StoreSubject<Rect | undefined>(undefined, { equals: rectStateEqual });
  readonly componentPlacement = new StoreSubject<ComponentPlacementState | undefined>(undefined);

  getCamera = (): Camera => {
    return this.camera.getValue();
  };

  setCamera = (camera: Camera): void => {
    this.camera.next(camera);
  };

  setHoveredNodeId = (nodeId: string | undefined): void => {
    this.hoveredNodeId.next(nodeId);
  };

  setPreviews = (previews: RenderPreview[]): void => {
    this.previews.next(previews);
  };

  setVertexPreview = (preview: VectorVertexPreviewState | undefined): void => {
    this.vertexPreview.next(preview);
  };

  setHandlePreview = (preview: VectorHandlePreviewState | undefined): void => {
    this.handlePreview.next(preview);
  };

  setEdgeBendPreview = (preview: VectorEdgeBendPreviewState | undefined): void => {
    this.edgeBendPreview.next(preview);
  };

  setSculptBrushPreview = (preview: SculptBrushPreviewState | undefined): void => {
    this.sculptBrushPreview.next(preview);
  };

  setVectorTopologyPreview = (preview: VectorTopologyPreviewState | undefined): void => {
    this.vectorTopologyPreview.next(preview);
  };

  setTextPreviews = (previews: Map<string, RichTextModel>): void => {
    this.textPreviews.next(previews);
  };

  updateTextPreviews = (updater: (current: Map<string, RichTextModel>) => Map<string, RichTextModel>): void => {
    this.setTextPreviews(updater(this.textPreviews.getValue()));
  };

  setBoundsPreview = (bounds: Rect | undefined): void => {
    this.boundsPreview.next(bounds);
  };

  updateBoundsPreview = (updater: (current: Rect | undefined) => Rect | undefined): void => {
    this.setBoundsPreview(updater(this.boundsPreview.getValue()));
  };

  setAnimationTimeMs = (timeMs: number): void => {
    this.animationTimeMs.next(timeMs);
  };

  bumpFontsReadyToken = (): void => {
    this.fontsReadyToken.next(this.fontsReadyToken.getValue() + 1);
  };

  bumpResetZoomToken = (): void => {
    this.resetZoomToken.next(this.resetZoomToken.getValue() + 1);
  };

  setViewportTarget = (target: ViewportTarget | undefined): void => {
    this.viewportTarget.next(target);
  };

  setViewportCenter = (center: { x: number; y: number } | undefined): void => {
    this.viewportCenter.next(center);
  };

  setViewportBounds = (bounds: Rect | undefined): void => {
    this.viewportBounds.next(bounds);
  };

  setComponentPlacement = (placement: ComponentPlacementState | undefined): void => {
    this.componentPlacement.next(placement);
  };

  clearVectorPreviews = (): void => {
    this.vertexPreview.next(undefined);
    this.handlePreview.next(undefined);
    this.edgeBendPreview.next(undefined);
    this.sculptBrushPreview.next(undefined);
    this.vectorTopologyPreview.next(undefined);
  };
}

export const viewStore = new ViewStore();
